/* eslint-disable max-len */
import React, { type PropsWithChildren } from "react";
import classNames from "classnames";
import { Link } from "react-router-dom";
import type { Component } from "../../content/schema-types";
import { useCtaClickHandler, useLocalizedUrl } from "../../hooks";
import { isStaticDownloadPath } from "../../utils/url-detection";

export type CtaButtonProps = Extract<Component, { type: "cta-button" }>["content"];

export type ButtonProps = Omit<CtaButtonProps, "text"> &
    PropsWithChildren<{
        className?: string;
        onDark?: boolean;
        onVideoClick?: (videoUrl: string) => void;
        onDemoClick?: (demoUrl: string) => void;
        /**
         * Handelt der Knopf auf der Seite selbst (z. B. oeffnet ein Modal),
         * statt irgendwohin zu fuehren?
         *
         * Ist er gesetzt, rendert die Komponente ein echtes `<button>` statt
         * eines `<Link>`. Das ist kein Kosmetik-Unterschied: ein Link ohne Ziel
         * kuendigt Screenreadern eine Navigation an, die nie passiert, und
         * `href="#"` springt an den Seitenanfang, wenn das Skript klemmt.
         */
        onClick?: () => void;
        "data-testid"?: string;
    }>;

export const Button: React.FC<ButtonProps> = ({
    href = "#",
    variant = "primary",
    size = "md",
    children,
    className = "",
    onDark = false,
    onClick,
    onVideoClick,
    onDemoClick,
    "data-testid": dataTestId,
}) => {
    const { handleCtaClick } = useCtaClickHandler({ onVideoClick, onDemoClick });
    const localizedHref = useLocalizedUrl(href);

    const isExternal = localizedHref.startsWith("http://") || localizedHref.startsWith("https://");

    const baseClasses =
        "inline-flex items-center justify-center text-center font-bold rounded-full !transition-all duration-300 ease-out hover:scale-110 hover:shadow-lg active:scale-105";

    const variantClasses: Record<string, string> = {
        primary: onDark
            ? "bg-secondary text-primary-dark hover:bg-secondary-dark active:bg-secondary-light transition-colors active:scale-100"
            : "bg-primary-dark text-white hover:bg-primary-dark active:bg-primary-light active:text-white transition-colors active:scale-100",
        secondary: onDark
            ? "box-border bg-transparent border-2 border-white text-white hover:text-white/80 hover:border-white/60 active:bg-white active:text-primary-dark transition-colors"
            : "box-border bg-transparent border-2 border-primary text-primary hover:bg-primary hover:text-white active:bg-primary-light active:text-white transition-colors",
        vp: "box-border border-2 border-transparent bg-primary-light text-neutral-50 hover:bg-primary shadow-xl py-4 px-12 transition-colors active:scale-100",
    };

    const sizeClasses: Record<string, string> = {
        sm: "box-border px-5 py-2 text-sm",
        md: "box-border px-6 py-2.5 text-base",
        lg: "box-border px-8 py-3 text-lg",
    };

    if (onClick) {
        return (
            <button
                type="button"
                onClick={onClick}
                className={classNames(baseClasses, variantClasses[variant], sizeClasses[size], className)}
                data-testid={dataTestId}
            >
                {children}
            </button>
        );
    }

    return (
        <Link
            to={localizedHref}
            reloadDocument={!isExternal && isStaticDownloadPath(localizedHref)}
            onClick={e => handleCtaClick(e, localizedHref)}
            {...(isExternal && { target: "_blank", rel: "noopener noreferrer" })}
            className={classNames(baseClasses, variantClasses[variant], sizeClasses[size], className)}
            data-testid={dataTestId}
        >
            {children}
        </Link>
    );
};

export default Button;
